import { useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import type { SocialOpportunityItem } from '@inference.engine/widgets/web';

import { getWidgetStores } from '@/entities/widget';
import { useBackTo } from '@/shared/lib/back-to';

import { SOCIAL_MEDIA_BRIEF_AGENT_ID } from './useCreateBrief.constants';
import type { UseCreateBriefDeps, UseCreateBriefReturn } from './useCreateBrief.types';

/** Stable fallback so we keep Rules of Hooks (always-call) when deps absent. */
const noopUseIsAiBusy = (): boolean => false;

/**
 * Cross-feature deps (`useIsAiBusy`, `sendOrCreateChat` from
 * `@/features/chats`) are INJECTED via the `deps` parameter (Springboard
 * dependency injection). The wiring site at
 * `@/lib/config/agents.config.ts` pre-binds them.
 *
 * Store access (`useChatStore`, `useChatStoreInput`) goes via the
 * `entities/widget` binding seam (`getWidgetStores().chatStore.*`) — same
 * pattern as image-widget / copy-widget.
 *
 * `deps` is OPTIONAL so legacy direct mounts (snapshot tests, dashboard
 * embedding) keep compiling. When omitted, the hook throws a fail-fast
 * error from inside the action callback (the lint config + dependency injection
 * wiring at the config layer ensure prod paths always inject).
 */
export const useCreateBrief = (
  selectedItem: SocialOpportunityItem | null,
  deps?: UseCreateBriefDeps,
): UseCreateBriefReturn => {
  const { t } = useTranslation('analysis');
  // Store access via the widget binding seam.
  const { chatStore } = getWidgetStores();
  const { setOptions } = chatStore.useChatStoreInput();
  const navigate = useNavigate();
  const backTo = useBackTo();
  // `useIsAiBusy` is injected via dependency injection. We must call the hook
  // unconditionally per Rules of Hooks, so fall back to a stable noop
  // selector when deps are absent (legacy mounts).
  const useIsAiBusyHook = deps?.useIsAiBusy ?? noopUseIsAiBusy;
  const isAiBusy = useIsAiBusyHook();

  const handleClose = useCallback(() => {
    const inputOptions = chatStore.useChatStore.getState().chatInput.options;
    if (inputOptions?.['selectedOpportunity']) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { selectedOpportunity, ...restOptions } = inputOptions;
      setOptions(restOptions);
    }
  }, [setOptions, chatStore.useChatStore]);

  const handleCreateBrief = useCallback(() => {
    if (!selectedItem) return;
    if (!deps?.sendOrCreateChat) {
      throw new Error(
        '[social-media-opportunities-widget] useCreateBrief: `deps.sendOrCreateChat` not injected. Wire it via `socialMediaOpportunitiesDeps` at the config-layer factory (`@/lib/config/agents.config.ts`).',
      );
    }

    void deps.sendOrCreateChat(navigate, {
      agentId: SOCIAL_MEDIA_BRIEF_AGENT_ID,
      prompt: t('socialMediaOpportunitiesWidget.useCreateBrief.createASocialMediaBrief', {
        opportunityTitle: selectedItem.opportunityTitle,
      }),
      inputs: { inputData: { selectedOpportunity: selectedItem.opportunityId } },
      backTo,
    });
  }, [selectedItem, navigate, deps, t, backTo]);

  return {
    handleCreateBrief,
    handleClose,
    isWaiting: isAiBusy,
  };
};
